/**
 * Read API for the site entity's normalized stores.
 *
 * One selector per part of the entity, all sharing the selectX(siteId)
 * signature consumers use. Absent collections return a stable frozen reference
 * (no spurious re-renders); load status is derived from the underlying
 * retrieveSite query, not duplicated into a slice.
 *
 * Contains:
 * - selectSiteData / selectSiteUnit / selectSiteAlarms / selectSiteWeather
 * - selectSiteFeatureAssociations / selectSiteFeatureAssociationByKey
 * - selectSitePermissions / selectSiteDashboardStatus
 */
import { siteApi } from './endpoints';

// Stable empty references so an absent collection returns the same value each
// call (no spurious consumer re-renders).
const EMPTY_ALARMS = Object.freeze([]);
const EMPTY_FEATURE_ASSOCIATIONS = Object.freeze([]);

/** @returns {function} selector returning the site object or null */
export const selectSiteData = siteId => state => state.site.bySiteId[String(siteId)] ?? null;

/** @returns {function} selector returning the unit object or null */
export const selectSiteUnit = siteId => state => state.siteUnit.bySiteId[String(siteId)] ?? null;

/** @returns {function} selector returning the active-alarm array (empty if none) */
export const selectSiteAlarms = siteId => state => state.siteAlarms.bySiteId[String(siteId)]?.list ?? EMPTY_ALARMS;

/** @returns {function} selector returning site.weather or null */
export const selectSiteWeather = siteId => state => state.site.bySiteId[String(siteId)]?.weather ?? null;

/** @returns {function} selector returning the feature_associations array (empty if none) */
export const selectSiteFeatureAssociations = siteId => state =>
  state.siteMeta.featureAssociationsBySiteId[String(siteId)] ?? EMPTY_FEATURE_ASSOCIATIONS;

/**
 * Returns the feature association for a specific feature key, or null if absent.
 *
 * @param {string|number} siteId
 * @param {string} featureKey
 * @returns {function} selector returning the feature association object or null
 */
export const selectSiteFeatureAssociationByKey = (siteId, featureKey) => state => {
  const associations = state.siteMeta.featureAssociationsBySiteId[String(siteId)] ?? EMPTY_FEATURE_ASSOCIATIONS;
  return associations.find(a => a.feature_key === featureKey) ?? null;
};

/** @returns {function} selector returning the per-(user,site) permissions object or null */
export const selectSitePermissions = siteId => state => state.siteMeta.permissionsBySiteId[String(siteId)] ?? null;

/**
 * Derive { loading, error } from the composite retrieve query. Skeleton only on
 * the genuine first load (no cached data yet); a background refetch over
 * existing data does not flip loading.
 *
 * @param {string|number} siteId
 * @returns {function} selector returning { loading, error }
 */
export const selectSiteDashboardStatus = siteId => state => {
  const result = siteApi.endpoints.retrieveSite.select({ id: Number(siteId) })(state);
  return {
    loading: result?.status === 'pending' && result?.data == null,
    error: result?.status === 'rejected' ? (result?.error?.data ?? result?.error ?? 'Failed to load dashboard') : null,
  };
};
